<script setup>
import {defineEmits, defineProps, ref, watch} from "vue";
import {Modal} from "ant-design-vue";
import {formatNewDate} from "@/utils/datetime";
import {isArray} from "@apollo/client/utilities";

const props = defineProps({
  visible: Boolean,
  initialData: {
    type: Object,
    default: null
  }
});

const set1 = ref([]);
const set2 = ref([]);
const set3 = ref([]);
const isLive = ref(true);
const isRevised = ref(false);
const revisedURL = ref("");

const matchData = ref(props.initialData);

const emit = defineEmits(['update:visible', 'success', 'cancel']);

const handleCancel = () => {
  emit('update:visible', false);
  emit('cancel');
};

const statusMatchMap = {
  NOT_SCHEDULED: {class: "badge bg-secondary", text: "Chưa lên lịch"},
  SCHEDULED: {class: "badge bg-info", text: "Đã lên lịch"},
  ONGOING: {class: "badge bg-primary", text: "Đang diễn ra"},
  PAUSED: {class: "badge bg-warning", text: "Tạm hoãn"},
  COMPLETED: {class: "badge bg-success", text: "Đã kết thúc"},
  CANCELED: {class: "badge bg-danger", text: "Đã huỷ"},
};

watch(
    () => props.initialData,
    (initData) => {
      if (initData) {
        matchData.value = initData;

        if (initData.tour_match_status === "COMPLETED") {
          isLive.value = false;
          isRevised.value = true;
          revisedURL.value = initData.revised_url;
        } else {
          isLive.value = true;
          isRevised.value = false;
        }

        const score_detail = initData.score_detail;
        if (score_detail) {
          const scrore_arr = JSON.parse(score_detail);

          set1.value = isArray(scrore_arr[0]) ? scrore_arr[0] : JSON.parse(scrore_arr[0]);
          set2.value = isArray(scrore_arr[1]) ? scrore_arr[1] : JSON.parse(scrore_arr[1]);
          set3.value = isArray(scrore_arr[2]) ? scrore_arr[2] : JSON.parse(scrore_arr[2]);
        } else {
          set1.value = set2.value = set2.value = [0, 0]
        }
      } else {
        console.log("no field")
      }
    },
);

</script>

<template>
  <Modal
      :open="props.visible"
      title="Xem thông tin chi tiết trận đấu"
      width="600px"
      okText="Đóng"
      cancelText="Hủy bỏ"
      @cancel="handleCancel"
      @ok="handleCancel"
  >
    <div class="row">
      <div class="col-lg-12">
        <div class="profile-tab">
          <ul class="nav nav-tabs" role="tablist">
            <li class="nav-item" role="presentation">
              <button id="match_info-tab1" aria-selected="true" class="nav-link active" data-bs-target="#match_info"
                      data-bs-toggle="tab" role="tab" type="button">
                Thông tin trận đấu
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button id="match_history-tab1" aria-selected="false" class="nav-link" data-bs-target="#match_history"
                      data-bs-toggle="tab" role="tab" type="button">
                Lịch sử trận đấu
              </button>
            </li>
          </ul>
          <div class="tab-content">
            <div id="match_info" class="tab-pane fade show active" role="tabpanel">
              <div class="profile-card mb-0">
                <div class="d-flex justify-content-end align-items-center w-100">
                  <a v-if="isRevised" :href="matchData?.replay_url ?? '#'" class="btn btn-primary" target="_blank">Xem
                    lại</a>
                </div>
              </div>

              <div class="profile-card mb-0 d-flex justify-content-between">
                <div class="w-50">
                  <div class="profile-card-title">
                    <h4>Đội 1</h4>
                  </div>
                  <div class="profile-contact-details mb-0 w-100">
                    <ul v-if="matchData?.tour_player_team_1?.player_1" class="w-100">
                      <li class="w-100">
                        <span>Tên vận động viên</span>
                        <h5>{{ matchData?.tour_player_team_1?.player_1?.display_name }}</h5>
                      </li>
                    </ul>
                    <ul v-if="matchData?.tour_player_team_1?.player_2" class="w-100">
                      <li class="w-100">
                        <span>Tên vận động viên</span>
                        <h5>{{ matchData?.tour_player_team_1?.player_2?.display_name }}</h5>
                      </li>
                    </ul>
                  </div>
                </div>

                <div class="w-50">
                  <div class="profile-card-title">
                    <h4>Đội 2</h4>
                  </div>
                  <div class="profile-contact-details mb-0 w-100">
                    <ul v-if="matchData?.tour_player_team_2?.player_1" class="w-100">
                      <li class="w-100">
                        <span>Tên vận động viên</span>
                        <h5>{{ matchData?.tour_player_team_2?.player_1?.display_name }}</h5>
                      </li>
                    </ul>
                    <ul v-if="matchData?.tour_player_team_2?.player_2" class="w-100">
                      <li class="w-100">
                        <span>Tên vận động viên</span>
                        <h5>{{ matchData?.tour_player_team_2?.player_2?.display_name }}</h5>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div class="profile-card mb-0">
                <div class="profile-card-title">
                  <h4>Nội dung thi đấu: {{ matchData?.category?.name }}</h4>
                </div>
                <div class="profile-contact-details mb-0">
                  <h6 class="mb-2">Vòng đấu: {{ matchData?.round?.name }}</h6>
                  <h6 class="mb-2">Ngày thi đấu: {{ formatNewDate(matchData?.start_date) }}</h6>
                  <h6 class="mb-2">Trạng thái: <span :class="statusMatchMap[matchData?.tour_match_status]?.class"> {{
                      statusMatchMap[matchData?.tour_match_status]?.text
                    }}</span></h6>
                  <h6>Nhà thi đấu: {{ matchData?.court?.arena?.name }} - Địa chỉ: {{ matchData?.court?.arena?.address }}
                    - Liên hệ: {{ matchData?.court?.arena?.contact_phone }}</h6>
                  <h6>Sân thi đấu: {{ matchData?.court?.name }}</h6>
                </div>
              </div>

              <div class="profile-card mb-0 w-100">
                <div class="profile-card-title">
                  <h4>Trọng tài</h4>
                </div>
                <div class="profile-contact-details mb-0 w-100">
                  <ul v-if="matchData?.umpire" class="w-100">
                    <li class="w-100">
                      <span>Tên trọng tài</span>
                      <h5>{{ matchData?.umpire.name }}</h5>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div id="match_history" class="tab-pane fade" role="tabpanel">
              <div class="review-box review-box-user d-flex">
                <div class="review-info">
                  <h5>Chi tiết trận</h5>
                  <div class="w-100">
                    <table class="table w-50">
                      <thead>
                      <tr>
                        <th scope="col">Vận động viên</th>
                        <th scope="col">Set 1</th>
                        <th scope="col">Set 2</th>
                        <th scope="col">Set 3</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr>
                        <th scope="row">
                          {{ matchData?.tour_player_team_1?.player_1?.display_name}}
                          <br>
                          {{ matchData?.tour_player_team_1?.player_2?.display_name}}
                        </th>
                        <td>{{ set1[0] }}</td>
                        <td>{{ set2[0] }}</td>
                        <td>{{ set3[0] }}</td>
                      </tr>
                      <tr>
                        <th scope="row">
                          {{ matchData?.tour_player_team_2?.player_1?.display_name}}
                          <br>
                          {{ matchData?.tour_player_team_2?.player_2?.display_name}}
                        </th>
                        <td>{{ set1[1] }}</td>
                        <td>{{ set2[1] }}</td>
                        <td>{{ set3[1] }}</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div class="review-box review-box-user d-flex">
                <div class="review-info">
                  <h5>Kết quả: <span class="ms-2 h4">{{
                      matchData?.score ? JSON.parse(matchData.score).join(" - ") : ""
                    }}</span></h5>
                </div>
              </div>

              <div class="review-box review-box-user d-flex">
                <div class="review-info">
                  <h5>Đội thắng:
                    <span class="ms-2 h4">{{
                        matchData?.winner?.player_1?.display_name ?? matchData?.winner?.player_2?.display_name
                      }}</span>
                  </h5>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>

<style scoped>

</style>